import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import {
    Button,
    makeStyles,
    Tag,
    TagPicker,
    TagPickerControl,
    TagPickerGroup,
    TagPickerInput,
    TagPickerList,
    TagPickerOption,
    TagPickerProps,
    Tooltip,
} from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { DismissRegular } from '@fluentui/react-icons'
import { TagPickerOnOptionSelectData } from '@fluentui/react-tag-picker'

const useStyles = makeStyles({
    root: { minWidth: '100px' },
    listbox: { maxHeight: '400px', minWidth: '300px' },
})

export type BaseTagPickerProps<E, G = E> = Omit<TagPickerProps, 'value' | 'children' | 'onOptionSelect'> & {
    entities: E[]
    onOptionsSelect: (e: Event | React.SyntheticEvent, selectedOptions: string[], selectedEntities: E[]) => void

    filter?: (entity: E) => boolean
    alphabetical?: boolean
    sort?: (a: E, b: E) => number

    placeholderPrefixKey?: string
    placeholderKey?: string
    singleSelect?: boolean

    isGroup?: (entity: E) => boolean
    getGroupEntities?: (group: G) => E[]
    getGroupValue?: (entity: G) => string
    getGroupLabel?: (entity: G) => string // A short-form label, used to fill the Avatar and the first part of the label.
    getGroupSecondaryContent?: (entity: G) => string // A second optional label, used as appendix in the list.
    getGroupClassName?: (entity: G) => string

    getEntityValue: (entity: E) => string // The key, most often the ID.
    getEntityLabel: (entity: E) => string // A short-form label.
    getEntitySecondaryContent?: (entity: E) => string // A second optional label, appended to getEntityLabel.
    getEntityTagLabel?: (entity: E) => string // The key used to generate the tag text in the input.
    getEntityTagSecondaryContent?: (entity: E) => string // A second optional tag label, used to show a tooltip.
}

function findEntities<E, G>(entities: E[], selectedEntities: string[], props: BaseTagPickerProps<E, G>): E[] {
    return entities
        .map(e => (props.isGroup?.(e) ? props.getGroupEntities?.(e as any) : [e]))
        .reduce((a, b) => a.concat(b), [])
        .filter(entity => selectedEntities.indexOf(props.getEntityValue(entity)) >= 0)
}

function applyOnOptionSelect<E, G>(
    e: Event | React.SyntheticEvent,
    data: TagPickerOnOptionSelectData,
    props: BaseTagPickerProps<E, G>,
): void {
    const selectedOptions = props.singleSelect
        ? data.selectedOptions.slice(data.selectedOptions.length - 1)
        : data.selectedOptions

    props.onOptionsSelect(e, selectedOptions, findEntities(props.entities, selectedOptions || [], props))
}

export default function BaseTagPicker<E, G = E>(props: BaseTagPickerProps<E, G>): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const entities: E[] = useMemo(
        () =>
            (props.entities || [])
                // .filter(entity => props.filter?.(entity) ?? true)
                .filter(
                    entity =>
                        !props.filter ||
                        props.filter(entity) ||
                        props.selectedOptions?.some(id => id === entity.toString()),
                )
                .sort((a, b) =>
                    props.alphabetical
                        ? props.getEntityLabel(a).localeCompare(props.getEntityLabel(b))
                        : props.sort
                          ? props.sort(a, b)
                          : 0,
                ),
        [props.entities],
    )
    const selectedEntities: E[] = useMemo(
        () => findEntities(entities, props.selectedOptions || [], props),
        [props.selectedOptions, entities],
    )
    const placeholder: string = useMemo(() => {
        if (selectedEntities.length > 0) {
            return undefined
        }

        const prefix =
            props.placeholderPrefixKey === ''
                ? ''
                : translate(props.placeholderPrefixKey ?? 'base.tagpicker.placeholder.prefix')
        const label =
            props.placeholderKey === ''
                ? ''
                : translate(props.placeholderKey ?? 'base.tagpicker.placeholder.option', {
                      count: props.singleSelect ? 1 : 100,
                  })

        return `${prefix} ${label}`
    }, [props.placeholderPrefixKey, props.placeholderKey, selectedEntities])

    const [query, setQuery] = React.useState<string>('')
    const queriedEntities: E[] = useMemo(() => {
        return (entities || []).filter(
            entity =>
                query.length === 0 ||
                props.getEntityLabel(entity)?.toLowerCase().indexOf(query?.toLowerCase()) >= 0 ||
                props.getEntitySecondaryContent?.(entity)?.toLowerCase().indexOf(query?.toLowerCase()) >= 0,
        )
    }, [props.entities, query])

    return (
        <div>
            <TagPicker
                onOptionSelect={(e, data) => {
                    setQuery('')
                    applyOnOptionSelect(e, data, props)
                }}
                selectedOptions={props.selectedOptions}
                {...props}
            >
                <TagPickerControl
                    className={styles.root}
                    secondaryAction={
                        !props.singleSelect &&
                        props.selectedOptions?.length > 0 && (
                            <Button
                                icon={<DismissRegular />}
                                appearance="transparent"
                                onClick={e => {
                                    setQuery('')
                                    props.onOptionsSelect(e, [], [])
                                }}
                            />
                        )
                    }
                >
                    <TagPickerGroup>
                        {selectedEntities.map(option => (
                            <Tag
                                key={props.getEntityValue(option)}
                                shape="rounded"
                                value={props.getEntityValue(option)}
                            >
                                <Tooltip
                                    content={props.getEntityTagSecondaryContent?.(option)}
                                    relationship="label"
                                    withArrow
                                >
                                    <div>{props.getEntityTagLabel?.(option) ?? props.getEntityLabel(option)}</div>
                                </Tooltip>
                            </Tag>
                        ))}
                    </TagPickerGroup>
                    <TagPickerInput placeholder={placeholder} value={query} onChange={e => setQuery(e.target.value)} />
                </TagPickerControl>
                <TagPickerList className={styles.listbox}>
                    {queriedEntities
                        .filter(option => !selectedEntities.includes(option))
                        .map((option: E, index: number) => (
                            <TagPickerOption
                                key={index}
                                value={props.getEntityValue(option)}
                                secondaryContent={props.getEntitySecondaryContent?.(option)}
                            >
                                {props.getEntityLabel(option)}
                            </TagPickerOption>
                        ))}
                </TagPickerList>
            </TagPicker>
        </div>
    )
}
